"use client";

import React, { useState } from "react";
import {
  ShieldCheck,
  Calendar,
  Building2,
  Stethoscope,
  Clock,
  ArrowRight,
  User,
  Heart,
  Award,
  AlertCircle,
} from "lucide-react";
import { InsurancePolicyLead } from "@/lib/demos/insuranceDb";
import InsuranceStageModal from "./InsuranceStageModal";
import InsuranceDetailModal from "./InsuranceDetailModal";

const STAGES: Array<{
  id: InsurancePolicyLead["stage"];
  label: string;
  badgeColor: string;
  dotColor: string;
}> = [
  {
    id: "Quote Generated",
    label: "Quote Generated",
    badgeColor: "bg-slate-900 border-slate-800 text-slate-300",
    dotColor: "bg-slate-400",
  },
  {
    id: "KYC Documents Ingested",
    label: "KYC Documents Ingested",
    badgeColor: "bg-blue-950/80 border-blue-800 text-blue-300",
    dotColor: "bg-blue-400",
  },
  {
    id: "Underwriting Review",
    label: "Underwriting Review",
    badgeColor: "bg-indigo-950/80 border-indigo-800 text-indigo-300",
    dotColor: "bg-indigo-400",
  },
  {
    id: "Premium Payment Received",
    label: "Premium Payment Received",
    badgeColor: "bg-cyan-950/80 border-cyan-800 text-cyan-300",
    dotColor: "bg-cyan-400",
  },
  {
    id: "Policy Issued (Won)",
    label: "Policy Issued (Won)",
    badgeColor: "bg-emerald-950/80 border-emerald-800 text-emerald-300",
    dotColor: "bg-emerald-400",
  },
  {
    id: "Annual Renewal Due",
    label: "Annual Renewal Due",
    badgeColor: "bg-amber-950/80 border-amber-800 text-amber-300",
    dotColor: "bg-amber-400",
  },
];

interface InsuranceKanbanProps {
  policies: InsurancePolicyLead[];
  onUpdatePolicy: (
    id: number,
    updates: Partial<InsurancePolicyLead> & { sendEmail: boolean }
  ) => Promise<void>;
}

export default function InsuranceKanban({
  policies,
  onUpdatePolicy,
}: InsuranceKanbanProps) {
  const [draggedPolicyId, setDraggedPolicyId] = useState<number | null>(null);
  const [activeStageModal, setActiveStageModal] = useState<{
    policy: InsurancePolicyLead;
    targetStage: InsurancePolicyLead["stage"];
  } | null>(null);
  const [selectedDetailPolicy, setSelectedDetailPolicy] =
    useState<InsurancePolicyLead | null>(null);

  // Drag handlers
  const handleDragStart = (e: React.DragEvent, id: number) => {
    e.dataTransfer.setData("text/plain", String(id));
    setDraggedPolicyId(id);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = async (
    e: React.DragEvent,
    targetStage: InsurancePolicyLead["stage"]
  ) => {
    e.preventDefault();
    const idStr = e.dataTransfer.getData("text/plain");
    const id = Number(idStr);
    setDraggedPolicyId(null);

    const policy = policies.find((p) => p.id === id);
    if (!policy || policy.stage === targetStage) return;

    // Prompt modal for critical operational stages
    if (
      targetStage === "Underwriting Review" ||
      targetStage === "Premium Payment Received" ||
      targetStage === "Policy Issued (Won)"
    ) {
      setActiveStageModal({ policy, targetStage });
    } else {
      await onUpdatePolicy(policy.id, { stage: targetStage, sendEmail: true });
    }
  };

  const handleConfirmStageModal = async (
    updates: Partial<InsurancePolicyLead> & { sendEmail: boolean }
  ) => {
    if (!activeStageModal) return;
    await onUpdatePolicy(activeStageModal.policy.id, updates);
  };

  // Quick Next Stage button
  const handleAdvanceOneStage = (policy: InsurancePolicyLead) => {
    const currentIndex = STAGES.findIndex((s) => s.id === policy.stage);
    if (currentIndex >= 0 && currentIndex < STAGES.length - 1) {
      const nextStage = STAGES[currentIndex + 1].id;
      if (
        nextStage === "Underwriting Review" ||
        nextStage === "Premium Payment Received" ||
        nextStage === "Policy Issued (Won)"
      ) {
        setActiveStageModal({ policy, targetStage: nextStage });
      } else {
        onUpdatePolicy(policy.id, { stage: nextStage, sendEmail: true });
      }
    }
  };

  return (
    <div className="w-full overflow-x-auto pb-6">
      <div className="flex gap-4 min-w-[1400px]">
        {STAGES.map((stageCol) => {
          const colPolicies = policies.filter((p) => p.stage === stageCol.id);

          return (
            <div
              key={stageCol.id}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, stageCol.id)}
              className="flex-1 min-w-[280px] max-w-[320px] bg-slate-900/60 border border-slate-800/80 rounded-2xl p-3 flex flex-col min-h-[620px]"
            >
              {/* Column Header */}
              <div className="flex items-center justify-between p-2 mb-3 border-b border-slate-800">
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${stageCol.dotColor}`} />
                  <h4 className="text-xs font-bold text-white tracking-tight">
                    {stageCol.label}
                  </h4>
                </div>
                <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-slate-800 text-slate-300">
                  {colPolicies.length}
                </span>
              </div>

              {/* Cards Container */}
              <div className="flex-1 space-y-3 overflow-y-auto pr-0.5">
                {colPolicies.map((policy) => {
                  const sym = policy.currency === "USD" ? "$" : "₹";

                  return (
                    <div
                      key={policy.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, policy.id)}
                      onClick={() => setSelectedDetailPolicy(policy)}
                      className={`p-4 rounded-xl bg-slate-950/90 border border-slate-800/90 hover:border-blue-600/60 shadow-md transition-all cursor-grab active:cursor-grabbing group hover:shadow-blue-950/40 relative ${
                        draggedPolicyId === policy.id ? "opacity-40" : ""
                      }`}
                    >
                      {/* Top Meta: Proposal Number & Currency */}
                      <div className="flex items-center justify-between text-[10px] text-slate-500 mb-2">
                        <span className="font-mono">{policy.proposal_number}</span>
                        <span className="text-slate-400 font-semibold">{policy.age} Yrs</span>
                      </div>

                      {/* Prospect Name & Policy Number */}
                      <div className="font-bold text-sm text-white group-hover:text-blue-300 transition-colors">
                        {policy.prospect_name}
                      </div>

                      {policy.policy_number && (
                        <div className="text-[10px] font-mono font-bold text-emerald-400 mt-0.5">
                          {policy.policy_number}
                        </div>
                      )}

                      {/* Plan & Sum Assured */}
                      <div className="mt-2.5 p-2.5 rounded-lg bg-slate-900 border border-slate-800/80">
                        <div className="text-[10px] text-slate-400 font-medium truncate">
                          {policy.product_name}
                        </div>
                        <div className="flex items-baseline justify-between mt-1">
                          <span className="text-xs font-bold text-blue-400">
                            {sym}{policy.coverage_amount.toLocaleString()} Cover
                          </span>
                          <span className="text-[10px] text-slate-300 font-semibold">
                            {sym}{policy.annual_premium.toLocaleString()}/yr
                          </span>
                        </div>
                      </div>

                      {/* Specific Stage Highlights */}
                      {policy.medical_exam_date && (
                        <div className="flex items-center gap-1.5 text-[10px] text-indigo-300 mt-2 truncate">
                          <Calendar className="w-3 h-3 text-indigo-400 shrink-0" />
                          <span className="truncate">{policy.medical_exam_date}</span>
                        </div>
                      )}

                      {policy.payment_reference && (
                        <div className="flex items-center gap-1.5 text-[10px] text-emerald-400 font-mono mt-1.5 truncate">
                          <Award className="w-3 h-3 text-emerald-400 shrink-0" />
                          <span className="truncate">{policy.payment_reference}</span>
                        </div>
                      )}

                      {/* Nominee Strip */}
                      {policy.nominee_name && (
                        <div className="flex items-center gap-1.5 text-[10px] text-slate-400 mt-2">
                          <Heart className="w-3 h-3 text-rose-400 shrink-0" />
                          <span>Nominee: {policy.nominee_name}</span>
                        </div>
                      )}

                      {/* Quick Advance Button */}
                      <div className="mt-3 pt-2.5 border-t border-slate-900 flex items-center justify-between">
                        <span className="text-[10px] text-slate-500">
                          {policy.kyc_status?.split(" ")[0] || "e-KYC OK"}
                        </span>

                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAdvanceOneStage(policy);
                          }}
                          title="Advance to next pipeline stage"
                          className="flex items-center gap-1 px-2 py-1 rounded bg-blue-600/10 hover:bg-blue-600 text-blue-400 hover:text-white text-[10px] font-bold transition-all cursor-pointer"
                        >
                          <span>Advance</span>
                          <ArrowRight className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  );
                })}

                {colPolicies.length === 0 && (
                  <div className="h-40 flex flex-col items-center justify-center text-center p-4 border border-dashed border-slate-800 rounded-xl text-slate-600 text-xs">
                    <span>No proposals</span>
                    <span className="text-[10px] text-slate-700 mt-1">Drag cards here</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Stage Transition Modal */}
      {activeStageModal && (
        <InsuranceStageModal
          policy={activeStageModal.policy}
          targetStage={activeStageModal.targetStage}
          onClose={() => setActiveStageModal(null)}
          onConfirm={handleConfirmStageModal}
        />
      )}

      {/* Applicant Dossier Detail Modal */}
      {selectedDetailPolicy && (
        <InsuranceDetailModal
          policy={selectedDetailPolicy}
          onClose={() => setSelectedDetailPolicy(null)}
        />
      )}
    </div>
  );
}
